var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import { createMuiTheme, ExpansionPanel, ExpansionPanelDetails, ExpansionPanelSummary, makeStyles, MuiThemeProvider, withStyles } from '@material-ui/core';
import styled from '@emotion/styled';
import { EMPTY_TEXT, Magnetic as Mi } from '@mitchell/react.component.ux';
import { DOWNLOAD_ARTIFACT } from '../constants';
/**
 * Styles for the twisty component using Material-UI's makeStyles
 */
const useStyles = makeStyles(theme => ({
    panelHeading: (props) => ({
        height: Mi.twistyBaseMinHeight,
        minHeight: Mi.twistyBaseMinHeight,
        display: 'flex',
        alignItems: 'center',
        paddingLeft: props.indentLevel ? `${props.indentLevel * 16}px` : Mi.spacer06
    }),
    headingLabel: (props) => {
        var _a;
        return ({
            fontSize: (_a = props.fontSize) !== null && _a !== void 0 ? _a : Mi.twistyBaseFontSize,
            color: Mi.twistyBaseColor,
            fontWeight: Mi.twistyBaseFontWeight
        });
    },
    headingSubLabel: (props) => {
        var _a;
        return ({
            fontSize: (_a = props.fontSize) !== null && _a !== void 0 ? _a : Mi.twistyBaseSubHeaderFontSize,
            fontWeight: Mi.twistyBaseSubHeaderFontWeight,
            color: Mi.twistyBaseColor,
            marginLeft: Mi.spacer08,
            whiteSpace: 'nowrap'
        });
    }
}));
/**
 * Styled ExpansionPanel for the twisty container
 */
const TwistyExpansionPanel = withStyles({
    root: {
        boxShadow: Mi.twistyBaseBoxShadow,
        '&:before': { background: 'none' },
        '&$expanded': { margin: '0' }
    },
    expanded: {}
})(ExpansionPanel);
/**
 * Styled ExpansionPanelSummary for the twisty header
 * Handles different background colors for parent vs child twisties
 */
const TwistyExpansionPanelSummary = withStyles({
    expandIcon: {
        '&$expanded': { transform: 'rotate(90deg)' },
        fontSize: Mi.twistyBaseIconSize,
        padding: Mi.twistyBaseIconPadding,
        order: -1,
        color: Mi.twistyBaseColor
    },
    root: {
        justifyContent: 'flex-start',
        backgroundColor: (props) => props.isChildTwisty ? '#ffffff' : Mi.twistyBaseBackgroundColor,
        '&:hover': { background: Mi.twistyRowBackgroundHover },
        '&:active': { backgroundColor: Mi.twistyRowBackgroundActive },
        '&$expanded': { minHeight: Mi.twistyBaseMinHeight }
    },
    content: { width: 0, alignItems: 'baseline' },
    expanded: {}
})((props) => {
    const { isChildTwisty, indentLevel } = props, rest = __rest(props, ["isChildTwisty", "indentLevel"]);
    return React.createElement(ExpansionPanelSummary, Object.assign({}, rest));
});
/**
 * Custom theme to disable ripple effect on buttons
 */
const theme = createMuiTheme({
    props: { MuiButtonBase: { disableRipple: true } }
});
/**
 * Styled ExpansionPanelDetails for the twisty content area
 * Handles indentation for nested content
 */
const StyledExpansionPanelDetails = withStyles({
    root: (props) => ({
        padding: 0,
        fontSize: Mi.baseFontSize,
        color: Mi.baseFontColor,
        paddingLeft: props.indentLevel ? `${(props.indentLevel + 1) * 16}px` : '4px'
    })
})((props) => {
    const { indentLevel } = props, rest = __rest(props, ["indentLevel"]);
    return React.createElement(ExpansionPanelDetails, Object.assign({}, rest));
});
/**
 * Styled div for handling text overflow in the header
 */
const StyledOverflowDiv = styled('div') `
	white-space: ${Mi.bannerBaseContentWhiteSpace};
	overflow: ${Mi.bannerBaseContentOverflow};
	text-overflow: ${Mi.bannerBaseContentTextOverflow};
`;
/**
 * Icon component for the twisty expand/collapse indicator
 */
const TwistyIcon = () => React.createElement("i", { className: "mi-icon-twisty-collapsed" });
/**
 * NestedTwistyComponent - An expandable/collapsible panel component that supports nesting
 *
 * This component provides a hierarchical display of content with expand/collapse functionality.
 * It supports different styling for parent and child levels and proper indentation for visual hierarchy.
 */
export const NestedTwisty = props => {
    const classes = useStyles(props);
    // Prepare the sub-heading label if provided
    const subHeadingLabel = props.headingSubLabel ? (React.createElement("span", { id: `${DOWNLOAD_ARTIFACT.ID_NESTED_TWISTY_SUB_HEADER_CONTENT}${props.id ? `_${props.id}` : EMPTY_TEXT}`, className: classes.headingSubLabel }, props.headingSubLabel)) : null;
    /**
     * Creates the expansion panel with all its parts
     */
    const getExpansionPanel = () => {
        const { shouldExpandByDefault, isChildTwisty, indentLevel } = props;
        return (React.createElement(TwistyExpansionPanel, { onChange: props.onToggle, defaultExpanded: shouldExpandByDefault, expanded: props.isExpanded, square: true },
            React.createElement(TwistyExpansionPanelSummary, { id: props.id || DOWNLOAD_ARTIFACT.ID_NESTED_TWISTY_HEADER_CONTENT, className: classes.panelHeading, expandIcon: React.createElement(TwistyIcon, null), IconButtonProps: { edge: 'start' }, isChildTwisty: isChildTwisty, indentLevel: indentLevel },
                React.createElement(StyledOverflowDiv, { id: `${DOWNLOAD_ARTIFACT.ID_NESTED_TWISTY_PARENT_CONTENT}${props.id ? `_${props.id}` : EMPTY_TEXT}`, "data-testid": `${DOWNLOAD_ARTIFACT.ID_NESTED_TWISTY_PARENT_CONTENT}${props.id ? `_${props.id}` : EMPTY_TEXT}`, className: classes.headingLabel },
                    props.label,
                    subHeadingLabel)),
            React.createElement(StyledExpansionPanelDetails, { id: `${DOWNLOAD_ARTIFACT.ID_NESTED_TWISTY_CHILD_CONTENT}${props.id ? `_${props.id}` : EMPTY_TEXT}`, "data-testid": `${DOWNLOAD_ARTIFACT.ID_NESTED_TWISTY_CHILD_CONTENT}${props.id ? `_${props.id}` : EMPTY_TEXT}`, indentLevel: indentLevel }, props.children)));
    };
    return React.createElement(MuiThemeProvider, { theme: theme }, getExpansionPanel());
};
//# sourceMappingURL=NestedTwisty.js.map